import React, { useState, useEffect } from "react";
import {
  Box, TextField, Button, Alert, Typography, CircularProgress,
  InputAdornment, IconButton
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import { getMispSettings, setMispKey, deleteMispKey } from "../../services/settings_services";

const MispKeyTab = () => {
  const [key, setKey] = useState("");
  const [configured, setConfigured] = useState(false);
  const [keyPreview, setKeyPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const data = await getMispSettings();
      setConfigured(data.configured);
      setKeyPreview(data.key_preview);
    } catch (e) { setError("Failed to load MISP settings"); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await setMispKey(key);
      setSuccess("MISP API key saved and validated successfully");
      setKey("");
      fetchStatus();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save key");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setSaving(true); setError(null);
    try {
      await deleteMispKey();
      setConfigured(false); setKeyPreview(null);
      setSuccess("MISP API key removed");
    } catch (e) { setError("Failed to remove key"); }
    setSaving(false);
  };

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>MISP Integration</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure the MISP API key for your organization. This key is used
        for threat intelligence dashboards and event publishing.
      </Typography>

      {configured && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
          MISP key configured (ending in {keyPreview})
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TextField
        fullWidth label="MISP API Key" variant="outlined"
        type={showKey ? "text" : "password"}
        value={key} onChange={(e) => setKey(e.target.value)}
        placeholder={configured ? "Enter new key to replace current" : "Paste your MISP API key"}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowKey(!showKey)}>
                {showKey ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={!key || saving}>
          {saving ? <CircularProgress size={20} /> : "Save & Validate"}
        </Button>
        {configured && (
          <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving}>
            Remove Key
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MispKeyTab;
